import { Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { AppService } from '../../../services/app.service';
import { Breadcrumb } from '../../../interfaces/breadcrumb';
import { NodesService } from '../../../services/nodes/nodes.service';
import { MessageSeverity } from '../../../enums/message-severity';
import { NodeResult } from '../../../models/net/result/node-result';
import { NodeFactory } from '../../../utilities/node-factory';
import { NavigationEnd, Router } from '@angular/router';
import { debounceTime, filter, fromEvent } from 'rxjs';
import { ViewChild, ElementRef, AfterViewInit } from '@angular/core';

@Component({
    selector: 'app-breadcrumbs',
    templateUrl: './breadcrumbs.component.html',
    styleUrls: ['./breadcrumbs.component.css']
})

export class BreadcrumbsComponent implements OnInit, AfterViewInit {

    public breadcrumbs: Breadcrumb[] = [];
    private hasHandledInitialNavigation: boolean = false;
    public measureText: string = "";

    @ViewChild("breadcrumbWrapper") breadcrumbWrapper!: ElementRef;
    @ViewChildren("textSpan") textSpans!: QueryList<ElementRef>;

    public constructor(public appService: AppService, private nodeService: NodesService, private router: Router) { }

    public ngOnInit(): void {

        this.nodeService.navigatedToNode.subscribe((node: NodeResult) => {

            this.hasHandledInitialNavigation = true;
            this.buildBreadcrumbs(node, this.nodeService.currentNodes);
            setTimeout(() => this.recalculateCollapseFlags(), 0); // Wait for DOM paint
        });

        this.nodeService.coldLoadCompleteObservable.subscribe(() => {

            if (!this.hasHandledInitialNavigation) {

                let currentRoute: string = this.router.url.split('?')[0];
                let currentNode: NodeResult | null = null;

                const currentUrl = window.location.href;
                const url = new URL(currentUrl);
                const hostParts = url.hostname.split('.');
                let currentSubdomain: string = hostParts.length > 1 ? hostParts[0] : "";

                if (currentRoute.startsWith("/")) {

                    currentRoute = currentRoute.slice(1);
                }

                if ((currentSubdomain == "" || currentSubdomain == "www") && (currentRoute == "/" || currentRoute == "" || currentRoute == "/at/night")) {

                    currentNode = NodeFactory.getRootNode(this.nodeService.currentNodes);

                } else if ((currentSubdomain == "mendls") && (currentRoute == "/" || currentRoute == "" || currentRoute == "/at/night")) {

                    currentNode = NodeFactory.getNodeById(48, this.nodeService.currentNodes!);

                } else {

                    if (currentRoute == "/at/night") {

                        currentRoute = "/";
                    }

                    currentNode = NodeFactory.getNodeByRoute(currentRoute, this.nodeService.currentNodes);
                }

                if (currentNode) {

                    this.buildBreadcrumbs(currentNode, this.nodeService.currentNodes);
                    setTimeout(() => this.recalculateCollapseFlags(), 0); // Wait for DOM paint

                } else { // special case where no matching node was discovered for the url

                    var leafRoute: string = currentRoute;
                    var leafBreadcrumbDisplayText: string = currentRoute.split("/").filter(Boolean).pop() || "";
                    var leafIconName: string = "public"; // "planet";

                    this.breadcrumbs.push({

                        displayText: "Lutz Angeles™",
                        route: "",
                        subdomain: "www",
                        iconName: "",
                        iconOverrideImagePath: "https://lutzangeles.blob.core.windows.net/app/www/lutzangelino.png",
                        isLastCrumb: false,
                        isDisplayTextCollapsed: false
                    });

                    if (currentSubdomain == "mendls") {

                        this.breadcrumbs.push({

                            displayText: "Mendls",
                            route: leafRoute,
                            subdomain: currentSubdomain,
                            iconName: leafIconName,
                            iconOverrideImagePath: "https://lutzangeles.blob.core.windows.net/app/mendls/mendls.png",
                            isLastCrumb: true,
                            isDisplayTextCollapsed: false
                        });
                    }

                    if (leafRoute != "" && leafRoute.toLowerCase() != "at/night") {

                        this.breadcrumbs.push({

                            displayText: leafBreadcrumbDisplayText,
                            route: leafRoute,
                            subdomain: currentSubdomain,
                            iconName: leafIconName,
                            iconOverrideImagePath: undefined,
                            isLastCrumb: true,
                            isDisplayTextCollapsed: false
                        });
                    }
                }

                this.hasHandledInitialNavigation = true;
            }
        });
    }

    public ngAfterViewInit(): void {

        fromEvent(window, 'resize').pipe(debounceTime(150)).subscribe(() => this.recalculateCollapseFlags());
    }

    private recalculateCollapseFlags = (): void => {

        const wrapperWidth = this.breadcrumbWrapper.nativeElement.offsetWidth;
        const spans = this.textSpans.toArray();
        const rightBufferWidth: number = 200;
        const availableWidth = wrapperWidth - rightBufferWidth;

        let totalTrailWidth = 0;

        for (let i = 0; i < this.breadcrumbs.length; i++) {

            const crumb = this.breadcrumbs[i];

            if (!crumb.isLastCrumb) {

                const spanWidth = spans[i]?.nativeElement.offsetWidth ?? 0;
                totalTrailWidth += spanWidth + 48;
            }
        }

        let overflow = totalTrailWidth - availableWidth;

        for (let i = 0; i < this.breadcrumbs.length; i++) { // collapse from the start until overflow is resolved

            const crumb = this.breadcrumbs[i];

            if (crumb.isLastCrumb) {

                crumb.isDisplayTextCollapsed = false;
                continue;
            }

            const spanWidth = spans[i]?.nativeElement.offsetWidth ?? 0;
            const estimatedCrumbWidth = spanWidth + 48;

            if (overflow > 0) {

                crumb.isDisplayTextCollapsed = true;
                overflow -= estimatedCrumbWidth;

            } else {

                crumb.isDisplayTextCollapsed = false;
            }
        }
    };


    private buildBreadcrumbs = (currentNode: NodeResult, nodesPool: NodeResult[] | null): void => {

        this.breadcrumbs = [];
        const isMobileLayout: boolean = this.appService.screenSize == 1;

        const pushCrumb = (node: NodeResult, isLast: boolean) => {

            this.breadcrumbs.push({

                displayText: isMobileLayout ? (node.ShortDisplayText ?? node.DisplayText) : node.DisplayText,
                route: node.Route,
                subdomain: node.VenueRootSubdomain ?? "",
                iconName: node.IconName,
                iconOverrideImagePath: node.IconOverrideImagePath,
                isLastCrumb: isLast,
                isDisplayTextCollapsed: false
            });
        };

        pushCrumb(currentNode, true);

        let parentNode = this.getNodeParentByNodeId(currentNode.NodeId, nodesPool);

        while (parentNode) {

            pushCrumb(parentNode, false);
            parentNode = this.getNodeParentByNodeId(parentNode.NodeId, nodesPool);
        }

        this.breadcrumbs.reverse();
    }

    private getNodeParentByNodeId = (nodeId: number, nodes: any): NodeResult | null => {

        for (let i = 0; i < nodes.length; i++) {

            let parent = nodes[i];
            let found = parent.Nodes.find((n: { NodeId: number }) => n.NodeId === nodeId);

            if (found) return parent;

            if (parent.Nodes.length > 0) {

                let nested = this.getNodeParentByNodeId(nodeId, parent.Nodes);

                if (nested) return nested;
            }
        }

        return null;
    }
}
